"use client";

import { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IconButton } from "@/ui";
import ContextMenu from "@components/shared/ContextMenu";
import {
  STATS_RANGE_OPTIONS,
  getPresetLabel,
} from "@lib/settings/dateRanges";

function headingLabel(value) {
  if (value?.preset === "custom") {
    return value.from && value.to ? `Activity ${value.from} – ${value.to}` : "Activity";
  }
  return `Activity ${getPresetLabel(value?.preset ?? "7d").toLowerCase()}`;
}

export default function StatsRangeHeading({ value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCustom, setShowCustom] = useState(value?.preset === "custom");
  const [customFrom, setCustomFrom] = useState(value?.from ?? "");
  const [customTo, setCustomTo] = useState(value?.to ?? "");

  const handleOpen = (event) => {
    setShowCustom(value?.preset === "custom");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (key) => {
    if (key === "custom") {
      setShowCustom(true);
      if (customFrom && customTo && customFrom <= customTo) {
        onChange({ preset: "custom", from: customFrom, to: customTo });
      }
      return;
    }
    onChange({ preset: key, from: null, to: null });
    setShowCustom(false);
    handleClose();
  };

  const handleCustomChange = (field, val) => {
    const nextFrom = field === "from" ? val : customFrom;
    const nextTo = field === "to" ? val : customTo;
    setCustomFrom(nextFrom);
    setCustomTo(nextTo);
    if (nextFrom && nextTo && nextFrom <= nextTo) {
      onChange({ preset: "custom", from: nextFrom, to: nextTo });
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ mb: 2, gap: 'var(--space-3)' }}
    >
      <Typography variant="h2">{headingLabel(value)}</Typography>
      <IconButton
        size="sm"
        id="stats-range-button"
        aria-label="Change date range"
        aria-haspopup="true"
        onClick={handleOpen}
      >
        <KeyboardArrowDownIcon sx={{ fontSize: "1.25rem", color: "var(--action-primary)" }} />
      </IconButton>
      <ContextMenu
        anchorEl={anchorEl}
        onClose={handleClose}
        labelledBy="stats-range-button"
      >
        {STATS_RANGE_OPTIONS.map((option) => (
          <MenuItem
            key={option.key}
            selected={value?.preset === option.key}
            onClick={() => handleSelect(option.key)}
          >
            {option.label}
          </MenuItem>
        ))}
        {showCustom && (
          <Box
            sx={{ display: "flex", gap: 1, p: 1.5 }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              type="date"
              size="small"
              label="From"
              slotProps={{ inputLabel: { shrink: true } }}
              value={customFrom}
              onChange={(e) => handleCustomChange("from", e.target.value)}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              slotProps={{ inputLabel: { shrink: true } }}
              value={customTo}
              onChange={(e) => handleCustomChange("to", e.target.value)}
              error={Boolean(customFrom && customTo && customTo < customFrom)}
              helperText={
                customFrom && customTo && customTo < customFrom
                  ? "Must be on or after From"
                  : undefined
              }
            />
          </Box>
        )}
      </ContextMenu>
    </Stack>
  );
}
